import React, { useState } from 'react';
import { withGoogleMap, GoogleMap, Polygon } from 'react-google-maps';
import { useSelector } from 'react-redux';

import { types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { fitMapToBoundsWithListings } from './SearchMap.helpers.js';
import config from '../../config';
import ControlElemZoomIn from './components/ControlElemZoomIn';
import ControlElemZoomOut from './components/ControlElemZoomOut';
import ControlElemUserLocation from './components/ControlElemUserLocation';
import ControlElemOpenMap from './components/ControlElemOpenMap';
import ControlElemCloseMap from './components/ControlElemCloseMap';
import ControlElemCloseMapMobile from './components/ControlElemCloseMapMobile';
import { getCurrentUserLocation } from '../../util/localStorage';
import ControlElemLoadingIndicator from './components/ControlElemLoadingIndicator';
import { useIsMobile } from '../../hooks/useIsMobile';

import CurrentLocationMarker from './components/CurrentLocationMarker.js';
import PrivacyNotification from './components/PrivacyNotification';
import InfoCardComponent from './InfoCardComponent';
import PriceLabelsInLocations from './PriceLabelsInLocations';
import { useMapListings } from './hooks/useMapListings.js';
import { useMapZoomSync } from './hooks/useMapZoomSync.js';

const { LatLng } = sdkTypes;

const {
    initialSearchAddress,
    maps: { supportedCountries },
    defaultCountry,
} = config;

const { country, countrySelected } = getCurrentUserLocation();
const { code } =
    supportedCountries[countrySelected] ||
    supportedCountries[country] ||
    supportedCountries[defaultCountry];

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';

/**
 * MapWithGoogleMap uses withGoogleMap HOC.
 * It handles some of the google map initialization states.
 */
export const MapWithGoogleMap = withGoogleMap(
    ({
        center,
        infoCardOpen,
        searchMapListingIdsStr,
        activeListingId,
        onSettingActiveListingId: onActivateListing,
        listingsVisited,
        onMarkingListingsAsVisited,
        createURLToListing,
        onListingClicked,
        onListingInfoCardClicked,
        onIdle,
        onMapLoad,
        mapComponentRefreshToken,
        onMapPanelToggle,
        mapPanelOpen,
        mapEl,
        location,
        filters,
        bounds,
        listingsScores,
        mapInitialized,
        searchInProgress,
        history,
    }) => {
        const entities = useSelector(state => state.marketplaceData.entities);

        const [zoom, setZoom] = useState(undefined);
        const zoomThreshold = 12;
        const listings = useMapListings({
            searchMapListingIdsStr,
            zoom,
            entities,
            zoomThreshold,
        });

        /**
         * in order for users to have more convinient
         * manipulation with a card on devices with small vieport
         * render a card outside the map
         */
        const [isMobile] = useIsMobile(768);

        const searchParams = parse(location.search, {
            latlng: ['origin'],
        });
        const { address } = searchParams;
        const currentZoom = mapEl ? mapEl.getZoom() : zoom;
        const currentZoomAvailable = typeof currentZoom === 'number';
        const zoomAvailable = typeof zoom === 'number';

        const defaultCenter = center ? center : new LatLng(47.309778043446734, 8.520086731256438);

        useMapZoomSync({
            zoom,
            currentZoom,
            zoomAvailable,
            currentZoomAvailable,
            setZoom,
        });

        const zoomPropMaybe =
            currentZoomAvailable && typeof zoom === 'number'
                ? {
                      zoom,
                  }
                : {};

        const addressIsNotSelected = address === initialSearchAddress[code].address;

        const onFittingMapBoundsWithListings = listings =>
            fitMapToBoundsWithListings({
                listings,
                map: mapEl,
                bounds,
                initialAddress: !!addressIsNotSelected,
            });

        return (
            <GoogleMap
                {...zoomPropMaybe}
                defaultCenter={defaultCenter}
                // defaultZoom={7}
                options={{
                    // Disable all controls except zoom
                    mapTypeControl: false,
                    scrollwheel: false,
                    fullscreenControl: false,
                    clickableIcons: false,
                    streetViewControl: false,

                    // When infoCard is open, we can't differentiate double click on top of card vs map.
                    disableDoubleClickZoom: !!infoCardOpen,
                    // /* Disabling default UI widgets */
                    disableDefaultUI: true,
                }}
                ref={onMapLoad}
                onIdle={onIdle}
                onZoomChanged={() => setZoom(mapEl.getZoom())}
            >
                <Polygon
                    key={1}
                    options={{
                        fillOpacity: 0,
                        strokeColor: '#1A2B49',
                        strokeOpacity: 1,
                        strokeWeight: 1,
                        clickable: false,
                    }}
                />
                <PriceLabelsInLocations
                    onMarkingListingsAsVisited={onMarkingListingsAsVisited}
                    onActivateListing={onActivateListing}
                    onListingClicked={onListingClicked}
                    onFittingMapBoundsWithListings={onFittingMapBoundsWithListings}
                    activeListingId={activeListingId}
                    mapComponentRefreshToken={mapComponentRefreshToken}
                    listingsVisited={JSON.stringify(listingsVisited || {})}
                    listingsScores={JSON.stringify(listingsScores || {})}
                    listings={JSON.stringify(listings || [])}
                />
                {infoCardOpen ? (
                    <InfoCardComponent
                        infoCardOpen={infoCardOpen}
                        onListingInfoCardClicked={onListingInfoCardClicked}
                        createURLToListing={createURLToListing}
                        mapComponentRefreshToken={mapComponentRefreshToken}
                        isMobile={isMobile}
                    />
                ) : null}
                {!mapPanelOpen && <ControlElemOpenMap onOpen={() => onMapPanelToggle(true)} />}
                {mapPanelOpen && <ControlElemCloseMap onClose={() => onMapPanelToggle(false)} />}
                {!infoCardOpen && mapPanelOpen && (
                    <ControlElemCloseMapMobile onClose={() => onMapPanelToggle(false)} />
                )}
                {zoomAvailable && <ControlElemZoomIn setZoom={setZoom} zoom={zoom} />}
                {zoomAvailable && <ControlElemZoomOut setZoom={setZoom} zoom={zoom} />}
                <ControlElemUserLocation
                    location={location}
                    filters={filters}
                    history={history}
                    mapEl={mapEl}
                />
                {(!mapInitialized || searchInProgress) && <ControlElemLoadingIndicator />}
                <CurrentLocationMarker searchParams={searchParams} />
                <PrivacyNotification mapPanelOpen={mapPanelOpen} />
            </GoogleMap>
        );
    }
);
